import './TextField.scss'

import React, { useCallback, useEffect, useRef, useState } from 'react'

import CircleLoader from '../../icons/animated/CircleLoader'
import Icon from '../Icon/Icon'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { formEvent } from '../Form/Form'
import t from '../../helpers/i18n'
import useDelayedRun from '../../hooks/useDelayedRun'

const TextField = ({
  name,
  label,
  size,
  mode,
  placeholder,
  maxLength,
  minLength,
  initialValue,
  password,
  required,
  readOnly,
  disabled,
  autoComplete,
  onChange,
  onValidation,
  ref
}) => {
  const [value, setValue] = useState(initialValue)
  const lastExecution = useRef()
  const [validatedValue, setValidatedValue] = useState(initialValue)
  const [validating, setValidating] = useState()
  const [errorMessage, setErrorMessage] = useState()
  const field = useRef()

  const validation = useCallback(
    (value) => {
      const empty = !value || value === ''
      if (empty) {
        if (required) {
          setValidatedValue(null)
          setErrorMessage(t('validations.Required'))
        } else {
          setValidatedValue('')
        }
        return
      }

      if (minLength && value.length < minLength) {
        setValidatedValue(null)
        setErrorMessage(t('A minium of %s characters are required', minLength))
        return
      }

      if (!onValidation) {
        setValidatedValue(value)
        return
      }

      const result = onValidation(value || '')
      if (typeof result === 'string') {
        setValidatedValue(null)
        setErrorMessage(result)
      } else if (!result) {
        setValidatedValue(value)
      } else {
        setValidating(true)
        const execution = {
          promise: Promise.resolve(result),
          cancelled: false
        }
        if (lastExecution.current) lastExecution.current.cancelled = true
        lastExecution.current = execution
        execution.promise
          .then(() => {
            if (!execution.cancelled) {
              setValidatedValue(value)
            }
          })
          .catch((error) => {
            if (!execution.cancelled) {
              setValidatedValue(null)
              setErrorMessage(error)
            }
          })
          .finally(() => {
            if (!execution.cancelled) setValidating(false)
          })
      }
    },
    [required, minLength, onValidation, setErrorMessage]
  )

  const [setDelayedValue] = useDelayedRun(validation, 1000, value)

  const handleChange = useCallback(() => {
    setErrorMessage(false)
    setValidating(false)
    setValue(field.current.value)
    setDelayedValue(field.current.value)
    if (lastExecution.current) lastExecution.current.cancelled = true
  }, [setErrorMessage, setValue, setDelayedValue])

  const handleBlur = useCallback(() => {
    setErrorMessage(false)
    if (value !== validatedValue) validation(value)
  }, [value, validatedValue, setErrorMessage, validation])

  useEffect(() => {
    if (ref) ref.current = validatedValue
    if (name) {
      field.current.dispatchEvent(
        formEvent({
          action: 'UPDATE',
          name,
          value: validatedValue
        })
      )
    }
  }, [ref, validatedValue, name])

  useEffect(() => {
    if (name) {
      field.current.dispatchEvent(
        formEvent({
          action: 'REQUIRED',
          name,
          required
        })
      )
    }
  }, [name, required])

  useEffect(() => {
    const element = field.current
    return () => {
      if (name) {
        element.dispatchEvent(
          formEvent({
            action: 'REMOVE',
            name
          })
        )
      }
    }
  }, [name])

  useEffect(() => {
    if (onChange) onChange(value)
  }, [value, onChange])

  return (
    <div className={classNames('textfield', '-mode-' + mode, '-size-' + size)}>
      {label && (
        <label
          className={classNames({
            'label-tiny-medium': size === 'field-sized',
            'label-small-medium': size === 'default',
            'label-field-sized-medium': size === 'medium',
          })}
        >
          {label}
        </label>
      )}
      <div className={classNames("-input-wrapper", { "-error": !disabled && !readOnly && errorMessage })}>
        {onValidation && (validating ? <CircleLoader /> : errorMessage ? <Icon size={14} icon='PendingIcon' /> : (value === validatedValue && value && value !== '') ? <Icon size={14} icon='CheckmarkIcon' /> : null) }
        <input
          className={classNames({
            'proper-noun-field-sized-basic':
              mode === 'entity-name' && size === 'field-sized',
            'proper-noun-default-basic':
              mode === 'entity-name' && size === 'default',
            'proper-noun-medium-basic':
              mode === 'entity-name' && size === 'medium',
            'field-value-field-sized':
              mode === 'simple' && size === 'field-sized',
            'field-value-default': mode === 'simple' && size === 'default',
            'field-value-medium': mode === 'simple' && size === 'medium',
            '-icon-space': !!onValidation,
            '-disabled': disabled,
            '-read-only': !disabled && readOnly
          })}
          ref={field}
          name={name}
          type={password ? 'password' : 'text'}
          defaultValue={initialValue}
          placeholder={placeholder}
          maxLength={maxLength}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={disabled}
          readOnly={readOnly}
          autoComplete={autoComplete}
        />
      </div>
      {errorMessage && !disabled && !readOnly && (
        <span
          className={classNames('text-label', 'color-error', {
            'caption-tiny-basic': size === 'field-sized' || size === 'default',
            'caption-small-basic': size === 'medium'
          })}
        >
          {errorMessage}
        </span>
      )}
    </div>
  )
}

TextField.propTypes = {
  name: PropTypes.string,
  label: PropTypes.string,
  size: PropTypes.oneOf(['field-sized', 'default', 'medium']),
  mode: PropTypes.oneOf(['simple', 'entity-name']),
  placeholder: PropTypes.string,
  maxLength: PropTypes.number,
  minLength: PropTypes.number,
  initialValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  password: PropTypes.bool,
  required: PropTypes.bool,
  readOnly: PropTypes.bool,
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
  onValidation: PropTypes.func
}

TextField.defaultProps = {
  mode: 'simple',
  size: 'default'
}

export default TextField
